import { Link, useLocation, useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard,
  CheckSquare,
  Calendar,
  Bell,
  Settings,
  Users,
  ChevronLeft,
  LogOut,
  Plus,
  FolderOpen,
  User,
  Bell as BellIcon,
  Palette,
  HelpCircle,
  ChevronDown,
  Shield,
  Pencil,
  Upload,
  Trash2,
  X,
  Check,
  MessageCircle,
  Briefcase,
  Package2,
  Home,
  Database,
} from 'lucide-react';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

// Sidebar için default menü elemanları
const defaultMenuItems = [
  { icon: LayoutDashboard, name: 'Gösterge Paneli', path: '/dashboard' },
  { icon: CheckSquare, name: 'Görevler', path: '/tasks' },
  { icon: Calendar, name: 'Takvim', path: '/calendar' },
  { icon: FolderOpen, name: 'Dosya Yönetimi', path: '/files' },
  { icon: Bell, name: 'Bildirimler', path: '/notifications' },
  { icon: FolderOpen, name: 'Projeler', path: '/projects' },
  { icon: MessageCircle, name: 'Sohbet', path: '/chat' },
  { icon: FolderOpen, name: 'Danışmanlık', path: '/consulting' },
  { icon: Users, name: 'Ekip', path: '/team' },
];

// Masaüstü için min–max genişlik değerleri
const MIN_SIDEBAR_WIDTH = 200; // px
const MAX_SIDEBAR_WIDTH = 310; // px

const Sidebar = ({ isOpen, onToggle, customMenuItems, isStockPanel, onUserProfileUpdate }) => {
  const { isDark } = useTheme();
  // Use custom menu items if provided, otherwise use default
  const menuItems = customMenuItems || defaultMenuItems;
  
  // CSS for hiding scrollbars on mobile
  const scrollbarStyles = `
    .scrollbar-hide {
      -ms-overflow-style: none;  /* IE and Edge */
      scrollbar-width: none;     /* Firefox */
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none;             /* Chrome, Safari and Opera */
    }
    
    @media (min-width: 1024px) {
      .lg\\:scrollbar-default {
        -ms-overflow-style: auto;
        scrollbar-width: auto;
      }
      .lg\\:scrollbar-default::-webkit-scrollbar {
        display: block;
        width: 6px;
      }
      .lg\\:scrollbar-default::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.1);
        border-radius: 3px;
      }
      .lg\\:scrollbar-default::-webkit-scrollbar-thumb {
        background: rgba(255, 255, 255, 0.3);
        border-radius: 3px;
      }
      .lg\\:scrollbar-default::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 255, 255, 0.5);
      }
    }
  `;
  
  // ------------------------------------------------
  // 1) Masaüstünde Sürüklenebilir (Resizable) Sidebar
  // ------------------------------------------------
  const [sidebarWidth, setSidebarWidth] = useState(288); // Varsayılan ~ 'w-72'
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      // Yeni genişlik: mevcut + hareket miktarı
      const newWidth = sidebarWidth + (e.clientX - startX);
      // Min–max arasında sınırla
      const clampedWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(newWidth, MAX_SIDEBAR_WIDTH));
      setSidebarWidth(clampedWidth);
      setStartX(e.clientX);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, startX, sidebarWidth]);

  const handleMouseDown = (e) => {
    setIsResizing(true);
    setStartX(e.clientX);
  };
  // ------------------------------------------------

  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const profileMenuRef = useRef(null);
  const photoInputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Takipçi ve takip sayıları için state
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isLoadingFollowCounts, setIsLoadingFollowCounts] = useState(true);

  const [crop, setCrop] = useState({ unit: '%', width: 100, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageRef, setImageRef] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [currentLogo, setCurrentLogo] = useState('');
  const [isLogoChanging, setIsLogoChanging] = useState(false);
  const prevThemeRef = useRef(isDark);
  const initialLoadRef = useRef(true);
  const [userRole, setUserRole] = useState(null);

  // Logo URL'leri - Stok paneli için yeşil logo ekle
  const logos = {
    dark: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent2.png",
    light: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent2_black.png",
    stockDark: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent2.png", // Aynı logo kullanılabilir ya da özel bir logo eklenebilir
    stockLight: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent2_black.png" // Aynı logo kullanılabilir ya da özel bir logo eklenebilir
  };

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Logo önbelleğe alma ve tema değişimini yönetme
  useEffect(() => {
    // Logoları önbelleğe al
    const preloadLogos = () => {
      return Promise.all(
        Object.values(logos).map(url => {
          return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(url);
            img.onerror = () => reject(new Error(`Failed to load: ${url}`));
            img.src = url;
          });
        })
      );
    };

    // Tema değişim olayını dinle
    const handleThemeChange = async ({ detail }) => {
      try {
        setIsLogoChanging(true);
        setLogoLoaded(false);

        // Yeni logo URL'sini ayarla - stok modunu kontrol et
        const newLogo = isStockPanel 
          ? (detail.isDark ? logos.stockDark : logos.stockLight)
          : (detail.isDark ? logos.dark : logos.light);
        setCurrentLogo(newLogo);
        setLogoLoaded(true);
      } catch (error) {
        console.error('Logo değişim hatası:', error);
        toast.error('Logo değiştirilirken bir hata oluştu');
      } finally {
        setIsLogoChanging(false);
      }
    };

    // İlk yükleme veya tema/panel değişim durumunda logo yükle
    const initializeLogo = async () => {
      // Eğer logo zaten yüklenmişse ve sadece sayfa navigasyonu yapılıyorsa, 
      // logoyu yeniden yükleme
      if (logoLoaded && !prevThemeRef.current !== isDark && initialLoadRef.current === false) {
        return;
      }

      try {
        // Önceki tema değerini kaydet
        prevThemeRef.current = isDark;
        
        // İlk yükleme değilse, logo değişimini göster
        if (!initialLoadRef.current) {
          setIsLogoChanging(true);
        }
        
        await preloadLogos();
        // Stok modu için farklı logo seçimi
        setCurrentLogo(
          isStockPanel 
            ? (isDark ? logos.stockDark : logos.stockLight)
            : (isDark ? logos.dark : logos.light)
        );
        setLogoLoaded(true);
        
        // İlk yükleme sonrası animasyonu devre dışı bırak
        if (initialLoadRef.current) {
          setTimeout(() => {
            initialLoadRef.current = false;
            setIsInitialLoad(false);
          }, 1000);
        } else {
          setIsLogoChanging(false);
        }
      } catch (error) {
        console.error('Logo yükleme hatası:', error);
        setCurrentLogo(logos.light); // Fallback
        setLogoLoaded(true);
        setIsLogoChanging(false);
      }
    };

    // Event listener'ı ekle
    window.addEventListener('themeChanged', handleThemeChange);
    initializeLogo();

    // Cleanup
    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
    };
  }, [isDark, isStockPanel]); // isStockPanel dependency ekledik
  
  // Logo bileşeni - memo ile gereksiz render'ları engelle
  const LogoComponent = React.memo(() => (
    <AnimatePresence mode="wait">
      {!isLogoChanging && logoLoaded ? (
        <motion.div
          key={`${isDark ? 'dark' : 'light'}-${isStockPanel ? 'stock' : 'job'}`}
          initial={isInitialLoad ? { opacity: 0, scale: 0.2 } : false}
          animate={isInitialLoad ? { 
            opacity: 1, 
            scale: 1,
            transition: { type: "spring", stiffness: 300, damping: 25 }
          } : { opacity: 1 }}
          className="relative w-full flex justify-center"
        >
          <motion.img 
            initial={isInitialLoad ? { opacity: 0, y: 20 } : false}
            animate={isInitialLoad ? { 
              opacity: 1, 
              y: 0,
              transition: { 
                delay: 0.1,
                type: "spring",
                stiffness: 300,
                damping: 25
              }
            } : { opacity: 1 }}
            alt="Logo" 
            className="w-48 h-auto mx-auto block mt-2 mr-6 select-none" 
            src={currentLogo}
            onLoad={() => setLogoLoaded(true)}
            onError={() => {
              console.error('Logo yüklenirken hata oluştu');
              setLogoLoaded(false);
              toast.error('Logo yüklenemedi. Lütfen internet bağlantınızı kontrol edin!');
            }}
            draggable={false}
          />
        </motion.div>
      ) : (
        <motion.div
          key="loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="w-48 h-12 mx-auto mt-2 mr-6"
        >
          <div className="w-full h-full rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-pulse">
            <div className="w-full h-full rounded-lg bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-shimmer" />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  ));

  // Dışarıya tıklayınca profil menüsünü kapatma
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Kullanıcı profilini çek
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('*')
            .eq('id', user.id)
            .single();

          if (error) {
            console.error('Error fetching profile:', error);
          } else {
            setUserProfile(profile);
            setUserRole(profile?.role); // Set user role
            
            // Takipçi ve takip sayılarını yükle
            await fetchFollowCounts(profile.email);
            
            if (onUserProfileUpdate) {
              onUserProfileUpdate(profile);
            }
          }
        }
      } catch (error) {
        console.error('Error in fetchUserProfile:', error);
      }
    };

    fetchUserProfile();
  }, []); // ← Dependency array'den onUserProfileUpdate'i kaldırdık

  // Separate useEffect for sending profile updates to parent
  useEffect(() => {
    if (userProfile && onUserProfileUpdate) {
      onUserProfileUpdate(userProfile);
    }
  }, [userProfile]); // ← Sadece userProfile değiştiğinde çalışsın

  // Takipçi ve takip sayılarını getir
  const fetchFollowCounts = async (email) => {
    try {
      setIsLoadingFollowCounts(true);
      
      // Önce email'e karşılık gelen UUID'yi bulalım
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', email)
        .single();
      
      if (userError) {
        console.error('User ID fetch error:', userError);
        throw userError;
      }
      
      if (!userData || !userData.id) {
        throw new Error('Kullanıcı ID\'si bulunamadı');
      }
      
      const userId = userData.id;
      
      // Takipçi sayısını getir (kullanıcıyı takip edenler)
      const { count: followerCount, error: followersError } = await supabase
        .from('follows')
        .select('id', { count: 'exact' })
        .eq('following_id', userId);
      
      if (followersError) throw followersError;
      
      // Takip edilen sayısını getir (kullanıcının takip ettikleri)
      const { count: followingCount, error: followingError } = await supabase
        .from('follows')
        .select('id', { count: 'exact' })
        .eq('follower_id', userId);
      
      if (followingError) throw followingError;
      
      // State'leri güncelle
      setFollowerCount(followerCount || 0);
      setFollowingCount(followingCount || 0);
    } catch (error) {
      console.error('Follow counts fetch error:', error);
      // Hata durumunda sayaçları sıfırla
      setFollowerCount(0);
      setFollowingCount(0);
    } finally {
      setIsLoadingFollowCounts(false);
    }
  };

// Bileşen içinde ekleyin:
const [isLoggingOut, setIsLoggingOut] = useState(false);

const handleLogout = async () => {
  // Zaten logout işlemi başlatıldıysa tekrar başlatmayalım
  if (isLoggingOut) return;
  setIsLoggingOut(true);

  try {
    // Eğer userProfile varsa online_status güncellemesini yapalım
    if (userProfile?.email) {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ online_status: false })
        .eq('email', userProfile.email);

      if (updateError) {
        console.error('Online status güncelleme hatası:', updateError);
        // Güncelleme hatası olsa bile logout işlemine devam ediyoruz
      }
    }

    // Supabase oturumu kapat
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }

    // Local storage'dan oturum verilerini temizle
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userProfile');

    // Interval'i temizle
    if (onlineCheckInterval.current) {
      clearInterval(onlineCheckInterval.current);
    }

    toast.success('Başarıyla çıkış yapıldı');
    navigate('/welcome');
  } catch (error) {
    console.error('Logout error:', error);
    toast.error('Çıkış yapılırken bir hata oluştu');
  } finally {
    setIsLoggingOut(false);
  }
};





const handleLogout_online_status = async () => {
  // Zaten logout işlemi başlatıldıysa tekrar başlatmayalım
  if (isLoggingOut) return;
  setIsLoggingOut(true);

  try {
    // Eğer userProfile varsa online_status güncellemesini yapalım
    if (userProfile?.email) {
      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ online_status: false })
        .eq('email', userProfile.email);

      if (updateError) {
        console.error('Online status güncelleme hatası:', updateError);
        // Güncelleme hatası olsa bile logout işlemine devam ediyoruz
      }
    }

    // Supabase oturumu kapat
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }

    // Local storage'dan oturum verilerini temizle
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userProfile');

    // Interval'i temizle
    if (onlineCheckInterval.current) {
      clearInterval(onlineCheckInterval.current);
    }

    // toast.success('Başarıyla çıkış yapıldı');
    navigate('/welcome');
  } catch (error) {
    console.error('Logout error:', error);
    toast.error('Çıkış yapılırken bir hata oluştu');
    navigate('/welcome');
    
  } finally {
    setIsLoggingOut(false);
  }
};

  

  // Profil fotoğrafı için varsayılan URL oluştur
  const getDefaultProfilePhotoUrl = (firstName = '', lastName = '') => {
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&background=3b82f6&color=fff`;
  };

  const onImageLoad = (e) => {
    setImageRef(e.target);
  };

  // Kırpma fonksiyonu
  const getCroppedImg = async () => {
    if (!imageRef || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.naturalWidth / imageRef.width;
    const scaleY = imageRef.naturalHeight / imageRef.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imageRef,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const croppedFile = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
        resolve(croppedFile);
      }, 'image/jpeg');
    });
  };

  // Profil fotoğrafı yönetimi
  const profilePhotoManager = {
    async removeFromStorage(photoUrl) {
      try {
        if (!photoUrl) throw new Error('Silinecek fotoğraf bulunamadı');
        // "profile-photos/" sonrasını ayıklayabilmek için:
        const path = photoUrl.replace(/^.*\/profile-photos\//, 'profile-photos/');
        const { error: removeError } = await supabase.storage.from('profile-photos').remove([path]);
        if (removeError) {
          console.error('Storage remove error:', removeError);
          throw new Error('Fotoğraf storage\'dan silinemedi');
        }
      } catch (error) {
        console.error('Storage remove error:', error);
        throw error;
      }
    },

    async uploadToStorage(file) {
      try {
        if (!file) throw new Error('Yüklenecek dosya bulunamadı');
        const fileExt = file.name.split('.').pop();
        const fileName = `profile-photos/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(fileName, file);

        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error('Fotoğraf yüklenemedi');
        }

        const { data } = supabase.storage.from('profile-photos').getPublicUrl(fileName);
        if (!data?.publicUrl) {
          throw new Error('Fotoğraf URL\'i alınamadı');
        }
        return data.publicUrl;
      } catch (error) {
        console.error('Storage upload error:', error);
        throw error;
      }
    },

    async removePhotoUrlFromDatabase(email) {
      try {
        const { data: currentProfile, error: checkError } = await supabase
          .from('user_profiles')
          .select('profile_photo_url')
          .eq('email', email)
          .single();

        if (checkError) {
          console.error('Profile check error:', checkError);
          throw new Error('Profil bilgileri alınamadı');
        }
        if (!currentProfile.profile_photo_url) {
          throw new Error('Zaten profil fotoğrafınız yok');
        }

        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            profile_photo_url: null,
            updated_at: new Date().toISOString(),
          })
          .eq('email', email);

        if (updateError) {
          console.error('Database update error:', updateError);
          throw new Error('Veritabanı güncellenemedi');
        }
        return currentProfile.profile_photo_url;
      } catch (error) {
        console.error('Database remove error:', error);
        throw error;
      }
    },

    async updateProfilePhotoUrl(email, photoUrl) {
      try {
        const { error: updateError } = await supabase
          .from('user_profiles')
          .update({
            profile_photo_url: photoUrl,
            updated_at: new Date().toISOString(),
          })
          .eq('email', email);

        if (updateError) {
          console.error('Profile update error:', updateError);
          throw new Error('Profil fotoğrafı güncellenemedi');
        }
        const { data: profile, error: fetchError } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('email', email)
          .single();

        if (fetchError) {
          console.error('Profile fetch error:', fetchError);
          throw new Error('Güncel profil bilgileri alınamadı');
        }
        return profile;
      } catch (error) {
        console.error('Update error:', error);
        throw error;
      }
    },
  };

  // Fotoğraf yükleme
  const handlePhotoUpload = async (file) => {
    try {
      if (!file) return;
      setIsLoading(true);

      // Eski fotoğrafı varsa storage'dan sil
      if (userProfile?.profile_photo_url) {
        await profilePhotoManager.removeFromStorage(userProfile.profile_photo_url);
      }
      // Yeni fotoğrafı yükle
      const publicUrl = await profilePhotoManager.uploadToStorage(file);
      // DB'de update
      const updatedProfile = await profilePhotoManager.updateProfilePhotoUrl(
        userProfile.email,
        publicUrl
      );
      setUserProfile(updatedProfile);

      toast.success('Profil fotoğrafı güncellendi');
      setShowPhotoModal(false);
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.message || 'Fotoğraf yüklenirken bir hata oluştu');
    } finally {
      setIsLoading(false);
      setIsEditing(false);
      setSelectedFile(null);
    }
  };

  // Fotoğrafı kaldırma
  const handlePhotoRemove = async () => {
    try {
      if (!userProfile?.email) {
        toast.error('Kullanıcı bilgileri bulunamadı');
        return;
      }
      setIsLoading(true);

      const oldPhotoUrl = await profilePhotoManager.removePhotoUrlFromDatabase(userProfile.email);
      if (oldPhotoUrl) {
        await profilePhotoManager.removeFromStorage(oldPhotoUrl);
      }
      setUserProfile({ ...userProfile, profile_photo_url: null });

      toast.success('Profil fotoğrafı başarıyla kaldırıldı');
      setShowPhotoModal(false);
    } catch (error) {
      console.error('Photo remove error:', error);
      if (error.message === 'Zaten profil fotoğrafınız yok') {
        toast.error('Zaten profil fotoğrafınız yok');
      } else {
        toast.error(error.message || 'Fotoğraf kaldırılırken bir hata oluştu');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Kırpılan fotoğrafı kaydet
  const handleSaveCrop = async () => {
    try {
      const croppedFile = await getCroppedImg();
      if (!croppedFile) return;
      await handlePhotoUpload(croppedFile);
    } catch (error) {
      console.error('Crop save error:', error);
      toast.error('Fotoğraf kırpılırken bir hata oluştu');
    }
  };

  const [onlineStatus, setOnlineStatus] = useState(false);
  const onlineCheckInterval = useRef(null);

  // Online durum kontrolü
  const checkOnlineStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.email) return;

      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('online_status')
        .eq('email', session.user.email)
        .single();

      if (error) {
        console.error('Online status check error:', error);
        return;
      }

      // Eğer online_status false ise, 5 saniye bekle ve tekrar kontrol et
      // Bu giriş sonrası veritabanı güncellenmesi için zaman tanır
      if (!profile.online_status) {
        console.warn('Online status false, 5 saniye sonra tekrar kontrol edilecek...');
        
        setTimeout(async () => {
          // Tekrar kontrol et
          const { data: recheck, error: recheckError } = await supabase
            .from('user_profiles')
            .select('online_status')
            .eq('email', session.user.email)
            .single();
            
          if (!recheckError && !recheck.online_status) {
        setOnlineStatus(false);
        toast.error('Oturumunuz başka bir yerde sonlandırılmış. Lütfen tekrar giriş yapınız.');
        handleLogout_online_status();
          } else {
            setOnlineStatus(true);
          }
        }, 5000);
      } else {
        setOnlineStatus(true);
      }
    } catch (error) {
      console.error('Online status check error:', error);
    }
  };

  // Periyodik online durum kontrolü
  useEffect(() => {
    // İlk kontrol için 3 saniye bekle (giriş sonrası)
    const initialDelay = setTimeout(() => {
    checkOnlineStatus();
    
      // Her 30 saniyede bir kontrol et (10 saniye çok sık)
      onlineCheckInterval.current = setInterval(checkOnlineStatus, 30000);
    }, 3000);

    return () => {
      clearTimeout(initialDelay);
      if (onlineCheckInterval.current) {
        clearInterval(onlineCheckInterval.current);
      }
    };
  }, []);

  // Bildirim Sayacı State'i
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
  // Okunmamış mesaj sayacı state'i
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [isLoadingMessages, setIsLoadingMessages] = useState(true);

  // Mevcut kullanıcıyı al
  const getCurrentUser = useCallback(async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    } catch (error) {
      console.error('Error getting current user:', error.message);
      return null;
    }
  }, []);

  // Okunmamış bildirimleri çek
  const fetchUnreadNotificationsCount = useCallback(async () => {
    try {
      setIsLoadingNotifications(true);
      const user = await getCurrentUser();
      
      if (!user) {
        setIsLoadingNotifications(false);
        return;
      }

      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      
      setUnreadNotificationCount(count || 0);
    } catch (error) {
      console.error('Error fetching unread notification count:', error.message);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [getCurrentUser]);

  // Okunmamış mesajları çek
  const fetchUnreadMessagesCount = useCallback(async () => {
    try {
      setIsLoadingMessages(true);
      const user = await getCurrentUser();
      
      if (!user) {
        setIsLoadingMessages(false);
        return;
      }

      // Kullanıcının üyesi olduğu konuşmaları conversation_members tablosundan çek
      const { data: membershipData, error: membershipError } = await supabase
        .from('conversation_members')
        .select('conversation_id')
        .eq('user_id', user.id);

      if (membershipError) throw membershipError;

      // Hiç konuşma yoksa 0 döndür
      if (!membershipData || membershipData.length === 0) {
        setUnreadMessageCount(0);
        setIsLoadingMessages(false);
        return;
      }

      // Konuşma ID'lerini çıkar
      const conversationIds = membershipData.map(item => item.conversation_id);
      let totalUnreadCount = 0;

      // Her konuşma için okunmamış mesajları çek
      for (const conversationId of conversationIds) {
        const { data: msgData, error: msgCountError } = await supabase
          .from('messages')
          .select(`
            id,
            message_status!left(
              is_read,
              user_id
            )
          `)
          .eq('conversation_id', conversationId)
          .neq('sender_id', user.id);

        if (!msgCountError && msgData) {
          // Okunmamış mesajları say
          const unreadCount = msgData.filter(msg => {
            // message_status dizisini kontrol et
            if (!msg.message_status || msg.message_status.length === 0) {
              return true; // Hiç durum kaydı yoksa okunmamış sayılır
            }
            
            // Mevcut kullanıcı için okunma durumunu kontrol et
            return !msg.message_status.some(status => 
              status.user_id === user.id && status.is_read === true
            );
          }).length;
          
          totalUnreadCount += unreadCount;
        }
      }
      
      setUnreadMessageCount(totalUnreadCount);
    } catch (error) {
      console.error('Error fetching unread message count:', error.message);
      // Hata durumunda sayacı sıfırla
      setUnreadMessageCount(0);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [getCurrentUser]);

  // Mesajları gerçek zamanlı takip et
  useEffect(() => {
    // İlk yüklemede mesajları çek
    fetchUnreadMessagesCount();
    
    let messagesSubscription = null;
    let messageStatusSubscription = null;
    
    // Gerçek zamanlı mesaj takibi
    const setupMessagesSubscription = async () => {
      const user = await getCurrentUser();
      if (!user) return;
      
      // İki tablo için ayrı abonelikler oluştur
      // 1. Messages tablosu için abonelik (yeni mesajlar geldiğinde)
      messagesSubscription = supabase
        .channel('sidebar_messages')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            // Yeni bir mesaj geldiğinde veya mesaj güncellendiğinde sayıyı güncelle
            fetchUnreadMessagesCount();
          }
        )
        .subscribe();

      // 2. Message_status tablosu için abonelik (mesajlar okundu olarak işaretlendiğinde)
      messageStatusSubscription = supabase
        .channel('sidebar_message_status')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'message_status'
          },
          (payload) => {
            // Okunma durumu değiştiğinde sayıyı güncelle
            fetchUnreadMessagesCount();
          }
        )
        .subscribe();
    };

    setupMessagesSubscription();
    
    return () => {
      // Cleanup function - abonelikleri güvenli şekilde kaldır
      if (messagesSubscription) {
        supabase.removeChannel(messagesSubscription);
      }
      if (messageStatusSubscription) {
        supabase.removeChannel(messageStatusSubscription);
      }
    };
  }, [fetchUnreadMessagesCount, getCurrentUser]);

  // Bildirimleri gerçek zamanlı takip et
  useEffect(() => {
    // İlk yüklemede bildirimleri çek
    fetchUnreadNotificationsCount();
    
    let notificationsSubscription = null;
    
    // Gerçek zamanlı bildirim takibi
    const setupRealtimeSubscription = async () => {
      const user = await getCurrentUser();
      if (!user) return;
      
      notificationsSubscription = supabase
        .channel('sidebar_notifications')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            // Yeni bir bildirim eklendiğinde
            if (payload.eventType === 'INSERT' && !payload.new.is_read) {
              setUnreadNotificationCount(prev => prev + 1);
            }
            // Bir bildirim güncellendiğinde (okunma durumu değiştiğinde)
            else if (payload.eventType === 'UPDATE') {
              if (!payload.old.is_read && payload.new.is_read) {
                // Bildirim okundu olarak işaretlendi
                setUnreadNotificationCount(prev => Math.max(0, prev - 1));
              } else if (payload.old.is_read && !payload.new.is_read) {
                // Bildirim okunmadı olarak işaretlendi
                setUnreadNotificationCount(prev => prev + 1);
              }
            }
            // Bir bildirim silindiğinde
            else if (payload.eventType === 'DELETE' && !payload.old.is_read) {
              setUnreadNotificationCount(prev => Math.max(0, prev - 1));
            }
          }
        )
        .subscribe();
    };

    setupRealtimeSubscription();
    
    return () => {
      // Cleanup function - aboneliği güvenli şekilde kaldır
      if (notificationsSubscription) {
        supabase.removeChannel(notificationsSubscription);
      }
    };
  }, [fetchUnreadNotificationsCount, getCurrentUser]);

  // Sistem tipi değiştirme işlevi
  const handleSystemSwitch = (isStock) => {
    // Zaten geçiş animasyonu varsa veya zaten aynı paneldeyse işlem yapma
    if (isTransitioning || (isStock === isStockPanel)) return;
    
    // Geçiş hedefini belirle
    const targetSystem = isStock ? 'stock' : 'job';
    setTransitionTo(targetSystem);
    setIsTransitioning(true);
    
    // Animasyon süresi sonunda yönlendirme yap (3 saniye)
    setTimeout(() => {
      // LocalStorage'da panel bilgisini güncelle
      localStorage.setItem('activePanel', targetSystem);
      
      // Yönlendirme yap
      if (isStock) {
        navigate('/stokflow');
      } else {
        navigate('/dashboard');
      }
      
      // Animasyon durumunu temizle
      setTimeout(() => {
        setIsTransitioning(false);
        setTransitionTo(null);
      }, 100); // Yönlendirme sonrası küçük bir temizleme süresi
      
    }, 3000); // Yükleme animasyonu süresi - 3 saniye
  };

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionTo, setTransitionTo] = useState(null); // 'job' veya 'stock'

  return (
    <>
      {/* Inject scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />
      
      {/* Geçiş Animasyonu Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
          >
            {/* Ana Arkaplan */}
            <motion.div
              initial={{ 
                x: transitionTo === 'stock' ? '-100%' : '100%',
                opacity: 0 
              }}
              animate={{ 
                x: 0,
                opacity: 1
              }}
              transition={{ 
                type: "spring", 
                stiffness: 100,
                damping: 25,
                duration: 1
              }}
              className={`absolute inset-0 ${
                transitionTo === 'stock' 
                  ? 'bg-gradient-to-br from-green-600 via-green-500 to-emerald-600' 
                  : 'bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600'
              } backdrop-blur-sm`}
            >
              {/* Animasyonlu arka plan parçacıkları */}
              <div className="absolute inset-0 overflow-hidden opacity-20">
                {/* Floating Particles */}
                {[...Array(15)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{
                      x: Math.random() * window.innerWidth,
                      y: Math.random() * window.innerHeight,
                      scale: Math.random() * 0.5 + 0.5,
                      opacity: Math.random() * 0.5 + 0.3,
                    }}
                    animate={{
                      x: [
                        Math.random() * window.innerWidth,
                        Math.random() * window.innerWidth,
                        Math.random() * window.innerWidth,
                      ],
                      y: [
                        Math.random() * window.innerHeight,
                        Math.random() * window.innerHeight,
                        Math.random() * window.innerHeight,
                      ],
                      rotate: Math.random() * 360,
                      opacity: [0.3, 0.7, 0.3]
                    }}
                    transition={{
                      duration: Math.random() * 8 + 4,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                    className={`absolute w-16 h-16 rounded-full ${
                      transitionTo === 'stock' 
                        ? 'bg-green-400' 
                        : 'bg-blue-400'
                    } filter blur-xl`}
                  />
                ))}
                  
                {/* Geometric shapes */}
                {[...Array(10)].map((_, i) => (
                  <motion.div
                    key={`shape-${i}`}
                    initial={{
                      x: Math.random() * window.innerWidth,
                      y: Math.random() * window.innerHeight,
                      scale: Math.random() * 0.3 + 0.2,
                      opacity: Math.random() * 0.3 + 0.1,
                      rotate: Math.random() * 180,
                    }}
                    animate={{
                      rotate: Math.random() > 0.5 ? 360 : -360,
                      scale: [
                        Math.random() * 0.3 + 0.2,
                        Math.random() * 0.5 + 0.3,
                        Math.random() * 0.3 + 0.2,
                      ],
                    }}
                    transition={{
                      duration: Math.random() * 10 + 10,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                    className={`absolute ${
                      i % 3 === 0 
                        ? 'w-32 h-32 rounded-lg'
                        : i % 3 === 1
                          ? 'w-24 h-24 rotate-45'
                          : 'w-20 h-20 rounded-full'
                    } ${
                      transitionTo === 'stock' 
                        ? 'border-2 border-green-300/30' 
                        : 'border-2 border-blue-300/30'
                    } filter blur-sm`}
                  />
                ))}
              </div>

              {/* Özel Radar/Pulse Efekti */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ 
                    scale: [0, 2, 0],
                    opacity: [0, 0.2, 0] 
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 0.2
                  }}
                  className={`w-96 h-96 rounded-full ${
                    transitionTo === 'stock'
                      ? 'bg-green-400/10 border border-green-300/20' 
                      : 'bg-blue-400/10 border border-blue-300/20'
                  }`}
                />
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ 
                    scale: [0, 2, 0],
                    opacity: [0, 0.15, 0] 
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatDelay: 0.2,
                    delay: 0.5
                  }}
                  className={`absolute w-96 h-96 rounded-full ${
                    transitionTo === 'stock'
                      ? 'bg-green-400/10 border border-green-300/20' 
                      : 'bg-blue-400/10 border border-blue-300/20'
                  }`}
                />
              </div>

              <div className="flex items-center justify-center h-full relative">
                {/* Ana Konteyner */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 25 }}
                  className={`bg-white/10 backdrop-blur-xl p-10 rounded-2xl flex flex-col items-center relative shadow-2xl ${
                    transitionTo === 'stock'
                      ? 'border border-green-300/20 shadow-green-900/20' 
                      : 'border border-blue-300/20 shadow-blue-900/20'
                  } max-w-lg w-full`}
                >
                  {/* Üst Köşe Dekoratif Öğe */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.6 }}
                    className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                  >
                    {transitionTo === 'stock' 
                      ? <Package2 className="w-5 h-5 text-white/80" />
                      : <Briefcase className="w-5 h-5 text-white/80" />
                    }
                  </motion.div>
                  
                  {/* Sol Alt Köşe Dekoratif Öğe */}
                  <motion.div 
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 }}
                    className="absolute -bottom-3 -left-3 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                  >
                    {transitionTo === 'stock' 
                      ? <Package2 className="w-5 h-5 text-white/80" />
                      : <Briefcase className="w-5 h-5 text-white/80" />
                    }
                  </motion.div>
                  
                  {/* Başlık Bölümü */}
                  <div className={`w-full mb-8 flex items-center justify-between pb-4 border-b ${
                    transitionTo === 'stock'
                      ? 'border-green-300/20' 
                      : 'border-blue-300/20'
                  }`}>
                    <div className="flex items-center gap-4">
                      <motion.div
                        initial={{ rotate: -30, opacity: 0 }}
                        animate={{ rotate: 0, opacity: 1 }}
                        transition={{ delay: 0.4, type: "spring" }}
                        className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                          transitionTo === 'stock'
                            ? 'bg-green-500/40 text-green-100' 
                            : 'bg-blue-500/40 text-blue-100'
                        }`}
                      >
                        {transitionTo === 'stock' ? (
                          <Package2 className="w-8 h-8" />
                        ) : (
                          <Briefcase className="w-8 h-8" />
                        )}
                      </motion.div>
                      
                      <div>
                        <motion.h3
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.5 }}
                          className="text-xs text-white/70 font-medium uppercase tracking-wider"
                        >
                          Sistem Değiştiriliyor
                        </motion.h3>
                        <motion.h2
                          initial={{ x: -30, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.6 }}
                          className="text-2xl font-bold text-white"
                        >
                          {transitionTo === 'stock' ? (
                            "Stok Takip Sistemi"
                          ) : (
                            "İş Takip Sistemi"
                          )}
                        </motion.h2>
                      </div>
                    </div>
                    
                    {/* Yükleniyor Numarası */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className={`text-lg font-mono font-bold ${
                        transitionTo === 'stock'
                          ? 'text-green-300' 
                          : 'text-blue-300'
                      }`}
                    >
                      <span className="text-xs mr-1">%</span>
                      <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 3, times: [0, 0.8, 1] }}
                      >
                        {Math.floor((Date.now() % 3000) / 30)}
                      </motion.span>
                    </motion.div>
                  </div>
                  
                  {/* Orta Bölüm - Ana Loading Animasyonu */}
                  <div className="flex w-full gap-8 mb-6">
                    {/* Sol Taraf - Spinner Animasyonu */}
                    <div className="flex-1 flex items-center justify-center">
                      <div className="relative w-28 h-28">
                        {/* Dış halka */}
                        <motion.div 
                          animate={{ rotate: 360 }}
                          transition={{ 
                            duration: 4, 
                            repeat: Infinity, 
                            ease: "linear" 
                          }}
                          className={`w-full h-full rounded-full border-4 ${
                            transitionTo === 'stock'
                              ? 'border-green-500/30 border-t-green-400 border-l-green-400/50' 
                              : 'border-blue-500/30 border-t-blue-400 border-l-blue-400/50'
                          }`}
                        />
                        
                        {/* Orta halka */}
                        <motion.div 
                          animate={{ rotate: -360 }}
                          transition={{ 
                            duration: 3, 
                            repeat: Infinity, 
                            ease: "linear" 
                          }}
                          className={`absolute top-4 left-4 right-4 bottom-4 rounded-full border-4 ${
                            transitionTo === 'stock'
                              ? 'border-green-400/30 border-r-green-400 border-t-green-400/80' 
                              : 'border-blue-400/30 border-r-blue-400 border-t-blue-400/80'
                          }`}
                        />
                        
                        {/* İç halka */}
                        <motion.div 
                          animate={{ rotate: 360 }}
                          transition={{ 
                            duration: 2, 
                            repeat: Infinity, 
                            ease: "linear" 
                          }}
                          className={`absolute top-8 left-8 right-8 bottom-8 rounded-full border-4 ${
                            transitionTo === 'stock'
                              ? 'border-green-300/30 border-b-green-300' 
                              : 'border-blue-300/30 border-b-blue-300'
                          }`}
                        />
                        
                        {/* Merkez İkon */}
                        <motion.div
                          animate={{ scale: [0.8, 1.1, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity }}
                          className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full ${
                            transitionTo === 'stock'
                              ? 'bg-green-400/60 text-white' 
                              : 'bg-blue-400/60 text-white'
                          } flex items-center justify-center`}
                        >
                          {transitionTo === 'stock' ? (
                            <Package2 className="w-5 h-5" />
                          ) : (
                            <Briefcase className="w-5 h-5" />
                          )}
                        </motion.div>
                      </div>
                    </div>
                    
                    {/* Sağ Taraf - İşlem Adımları */}
                    <div className="flex-1 flex flex-col justify-center text-white">
                      <motion.h4
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                        className="text-sm font-medium mb-4"
                      >
                        Hazırlanıyor...
                      </motion.h4>
                      
                      {/* İşlem Adımları */}
                      <div className="space-y-3">
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.7 }}
                          className="flex items-center gap-2 text-sm"
                        >
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ delay: 0.9, duration: 0.5, times: [0, 0.5, 1] }}
                            className={`w-5 h-5 ${
                              transitionTo === 'stock'
                                ? 'text-green-300' 
                                : 'text-blue-300'
                            }`}
                          >
                            <Check />
                          </motion.div>
                          <span className="opacity-90">Sistem parametreleri kontrol ediliyor</span>
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.0 }}
                          className="flex items-center gap-2 text-sm"
                        >
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ delay: 1.6, duration: 0.5, times: [0, 0.5, 1] }}
                            className={`w-5 h-5 ${
                              transitionTo === 'stock'
                                ? 'text-green-300' 
                                : 'text-blue-300'
                            }`}
                          >
                            <Check />
                          </motion.div>
                          <span className="opacity-90">Kullanıcı yetkileri doğrulanıyor</span>
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.3 }}
                          className="flex items-center gap-2 text-sm"
                        >
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className={`w-4 h-4 border-2 rounded-full ${
                              transitionTo === 'stock'
                                ? 'border-green-300/50 border-t-green-300' 
                                : 'border-blue-300/50 border-t-blue-300'
                            }`}
                          />
                          <span className="opacity-90">
                            {transitionTo === 'stock' ? (
                              "Stok verileri yükleniyor"
                            ) : (
                              "Proje verileri yükleniyor"
                            )}
                          </span>
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 1.6 }}
                          className="flex items-center gap-2 text-sm opacity-60"
                        >
                          <div className="w-5 h-5">
                            <motion.div 
                              animate={{ opacity: [0.3, 1, 0.3] }}
                              transition={{ duration: 1.5, repeat: Infinity }}
                              className="w-1.5 h-1.5 rounded-full bg-white"
                            />
                          </div>
                          <span>
                            {transitionTo === 'stock' ? (
                              "Arayüz bileşenleri hazırlanıyor"
                            ) : (
                              "Görev yönetim arayüzü hazırlanıyor"
                            )}
                          </span>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Alt Bölüm - İlerleme Çubuğu */}
                  <div className="w-full">
                    <div className="flex items-center justify-between text-xs text-white/70 mb-2">
                      <span>İşlem tamamlanıyor</span>
                      <motion.span
                        animate={{ opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        Lütfen bekleyin...
                      </motion.span>
                    </div>
                    
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 2.8, ease: "easeInOut" }}
                        className={`h-full ${
                          transitionTo === 'stock' 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                            : 'bg-gradient-to-r from-blue-500 to-indigo-400'
                        }`}
                      />
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/*
        Sidebar'ın ana container'ı:
        - Hafif saydamlık + blur: bg-white/20 + backdrop-blur-xl
        - Mobilde isOpen'e göre sola gizle/göster
        - Masaüstünde (lg:) her zaman açık, sürüklenebilir genişlik
      */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50
          border-r border-white/900 dark:border-gray-700/50
          backdrop-blur-xl bg-white/20 dark:bg-gray-800/20
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
        style={{ width: sidebarWidth }}
      >
        {/* Üst Kısım (Logo vb.) */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-white/20 dark:border-gray-700/50">
          <div className="flex justify-center items-center w-full">
            <LogoComponent />
          </div>

          {/* Bu buton sadece mobilde gözüksün (lg:hidden) */}
          <button
            onClick={onToggle}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* İçerik Alanı */}
        <div className="flex flex-col h-[calc(100vh-4rem)]">
          {/* Yeni Görev Butonu - Stok modunda farklı buton göster */}
          <div className="p-4">
            {isStockPanel ? (
              <Link
                to="/stokflow/products/create"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Yeni Ürün</span>
              </Link>
            ) : (
              <Link
                to="/tasks/create"
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Yeni Görev</span>
              </Link>
            )}
          </div>

          {/* Menü Linkleri */}
          <nav className="flex-1 px-3 space-y-1 overflow-y-auto lg:overflow-y-auto scrollbar-hide lg:scrollbar-default">
            {menuItems.map((item) => {
              const Icon = item.icon;
              // Aktif sayfayı kontrol ederken hem normal yolu hem de stokflow versiyonunu kontrol et
              const isActive = location.pathname === item.path || location.pathname === `/stokflow${item.path}`;
              
              // Ortak menüler için özel işlem - dosya, chat, team
              const isSharedMenu = ['/files', '/chat', '/team'].includes(item.path);
              
              // Eğer Stok modunda ve ortak menüyse, Stok modu için özel URL'yi belirle
              let finalPath = item.path;
              if (isStockPanel && isSharedMenu) {
                // "/files" -> "/stokflow/files"
                // "/chat" -> "/stokflow/chat"
                // "/team" -> "/stokflow/team"
                finalPath = `/stokflow${item.path}`;
              }

              return (
                <Link
                  key={item.path}
                  to={finalPath}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? isStockPanel
                        ? 'bg-green-50/40 dark:bg-green-900/20 text-green-600 dark:text-green-400'
                        : 'bg-primary-50/40 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                      : 'text-gray-900 dark:text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      isActive
                        ? isStockPanel
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-900 lg:text-gray-400 dark:text-gray-500'
                    }`}
                  />
                  <span className="font-medium">{item.name}</span>
                  {item.name === 'Bildirimler' && unreadNotificationCount > 0 && (
                    <span className="ml-auto bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-red-900/20 dark:text-red-400 animate-pulse">
                      {isLoadingNotifications ? (
                        <div className="h-3 w-3 rounded-full border-2 border-red-600 dark:border-red-400 border-t-transparent animate-spin"></div>
                      ) : (
                        unreadNotificationCount
                      )}
                    </span>
                  )}
                  {item.name === 'Sohbet' && unreadMessageCount > 0 && (
                    <span className="ml-auto bg-blue-100 text-blue-600 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-blue-900/20 dark:text-blue-400 animate-pulse">
                      {isLoadingMessages ? (
                        <div className="h-3 w-3 rounded-full border-2 border-blue-600 dark:border-blue-400 border-t-transparent animate-spin"></div>
                      ) : (
                        unreadMessageCount
                      )}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Sistem Geçiş Butonu (İş Takip <-> Stok Takip) */}
          <div className="px-5 py-3 border-t border-white/20 dark:border-gray-700/50">
            <div className="flex flex-col items-center space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Sistem değiştir
              </p>
              
              <div className="w-full p-1.5 bg-gray-100 dark:bg-gray-800/80 rounded-full flex relative shadow-inner" style={{ zIndex: 1 }}>
                {/* Sliding Indicator */}
                <motion.div 
                  className={`absolute top-1.5 bottom-1.5 w-[calc(50%-4px)] rounded-full shadow-lg ${
                    isStockPanel 
                      ? 'bg-green-500 dark:bg-green-600' 
                      : 'bg-blue-500 dark:bg-blue-600'
                  }`}
                  style={{ zIndex: 5 }}
                  initial={false}
                  animate={{ 
                    x: isStockPanel ? 'calc(100% - 4px)' : '2px',
                  }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                />
                
                {/* İş Takip Butonu */}
                <button
                  onClick={() => handleSystemSwitch(false)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full transition-colors text-sm font-medium relative ${
                    !isStockPanel 
                      ? 'text-white' 
                      : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  style={{ zIndex: !isStockPanel ? 10 : 6 }}
                >
                  <Briefcase className="w-4 h-4" />
                  <span>İş Takip</span>
                </button>
                
                {/* Stok Takip Butonu */}
                <button
                  onClick={() => handleSystemSwitch(true)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-full transition-colors text-sm font-medium relative ${
                    isStockPanel 
                      ? 'text-white' 
                      : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  style={{ zIndex: isStockPanel ? 10 : 6 }}
                >
                  <Package2 className="w-4 h-4" />
                  <span>Stok Takip</span>
                </button>
              </div>
            </div>
          </div>

          {/* Profil ve Çıkış Alanı */}
          <div className="p-4 border-t border-white/20 dark:border-gray-700/50">
            <div className="relative" ref={profileMenuRef}>
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/10 transition-colors"
              >
                {userProfile ? (
                  <>
                    <div
                      className="relative group"
                      onMouseEnter={() => setShowPhotoMenu(true)}
                      onMouseLeave={() => setShowPhotoMenu(false)}
                    >
                      <img
                        src={
                          userProfile?.profile_photo_url ||
                          getDefaultProfilePhotoUrl(userProfile?.first_name, userProfile?.last_name)
                        }
                        alt={`${userProfile?.first_name || 'User'} ${userProfile?.last_name || ''}`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      {/* Online Status Indicator */}
                      <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-white dark:border-gray-800 rounded-full ${
                        onlineStatus ? 'bg-green-400' : 'bg-red-600'
                      }`}></div>
                      {/* Fotoğrafın üzerinde kalem ikonu (hover) */}
                      {showPhotoMenu && (
                        <div
                          className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowPhotoModal(true);
                          }}
                        >
                          <Pencil className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate text-left animate-fade-in">
                        {`${userProfile.first_name} ${userProfile.last_name}`}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate text-left animate-slide-in">
                        {userProfile.email}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Skeleton Loading Animation */}
                    <div className="relative">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-pulse">
                        <div className="w-full h-full rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-shimmer"></div>
                      </div>
                      {/* Skeleton Online Status */}
                      <div className="absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-white dark:border-gray-800 rounded-full bg-gray-300 dark:bg-gray-600 animate-pulse"></div>
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Skeleton Name */}
                      <div className="h-4 w-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                      {/* Skeleton Email */}
                      <div className="h-3 w-40 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                    </div>
                  </>
                )}
                <ChevronDown
                  className={`w-4 h-4 text-gray-400 transition-transform ${
                    showProfileMenu ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Profil Menüsü */}
              <AnimatePresence>
                {showProfileMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute bottom-full left-0 w-full mb-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2"
                    style={{ zIndex: 50 }}
                  >
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3 mb-2">
                        {userProfile ? (
                          <>
                            <img
                              src={
                                userProfile?.profile_photo_url ||
                                getDefaultProfilePhotoUrl(userProfile?.first_name, userProfile?.last_name)
                              }
                              alt={`${userProfile?.first_name || 'User'} ${userProfile?.last_name || ''}`}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                                {`${userProfile.first_name} ${userProfile.last_name}`}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {userProfile?.job_position || 'Pozisyon belirtilmemiş'}
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            {/* Skeleton Loading for Profile Menu */}
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 animate-pulse"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-4 w-32 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                              <div className="h-3 w-24 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        {userProfile ? (
                          isLoadingFollowCounts ? (
                            <>
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <div className="h-3 w-6 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                              </div>
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                <div className="h-3 w-6 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                <span>{followerCount} Takipçi</span>
                              </div>
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                <span>{followingCount} Takip</span>
                              </div>
                            </>
                          )
                        ) : (
                          <>
                            <div className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              <div className="h-3 w-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                            </div>
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <div className="h-3 w-16 bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 rounded animate-pulse"></div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="py-1">
                      {/* Menüleri kaydırılabilir alan içine al */}
                      <div className="max-h-60 overflow-y-auto scrollbar-hide lg:max-h-none lg:overflow-visible">
                        <Link
                          to={isStockPanel ? "/" : "/welcome"}
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                        >
                          <Home className="w-4 h-4" />
                          Ana Menüye Dön
                        </Link>
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                          <Link
                            to="/profile-info"
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                          >
                            <User className="w-4 h-4" />
                            Profil Bilgileri
                          </Link>
                          <Link
                            to="/account-settings"
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                          >
                            <Settings className="w-4 h-4" />
                            Hesap Ayarları
                          </Link>
                          <Link
                            to="/security-privacy"
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                          >
                            <Shield className="w-4 h-4" />
                            Güvenlik ve Gizlilik
                          </Link>
                        </div>
                        {/* Admin Section - Only show for super admins */}
                        {userRole === 'super' && (
                          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                            <Link
                              to="/admin/data-integrity"
                              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                            >
                              <Database className="w-4 h-4" />
                              Veri Bütünlüğü Kontrolü
                            </Link>
                          </div>
                        )}
                        <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                          <Link
                            to="/notification-preferences"
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                          >
                            <BellIcon className="w-4 h-4" />
                            Bildirim Tercihleri
                          </Link>
                        </div>
                        <Link
                          to="/theme-preferences"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                        >
                          <Palette className="w-4 h-4" />
                          Görünüm
                        </Link>
                        <Link
                          to="/help-support"
                          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-white/10"
                        >
                          <HelpCircle className="w-4 h-4" />
                          Yardım ve Destek
                        </Link>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-600/10"
                      >
                        <LogOut className="w-4 h-4" />
                        Çıkış Yap
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Masaüstü için sürükleme çubuğu (hidden lg:block) */}
        <div
          onMouseDown={handleMouseDown}
          className="hidden lg:block absolute top-0 right-0 w-2 h-full cursor-col-resize z-50 bg-transparent"
        />
      </div>

      {/*
        Mobil görünümde sidebar açıkken
        arka plana hafif siyah ve blur ekleyen overlay
      */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-md lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Fotoğraf Düzenleme Modal */}
      <AnimatePresence>
        {showPhotoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center px-4"
          >
            {/* Arkaplan */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30 dark:bg-black/80 backdrop-blur-xl"
              onClick={() => {
                setShowPhotoModal(false);
                setIsEditing(false);
                setSelectedFile(null);
              }}
            />

            {/* Modal İçeriği */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="relative max-w-md w-full bg-white/10 dark:bg-gray-800/50 backdrop-blur-xl border border-white/20 dark:border-gray-700/50 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Başlık */}
              <div className="relative p-6 pb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Profil Fotoğrafını Düzenle
                  </h3>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                      setShowPhotoModal(false);
                      setIsEditing(false);
                      setSelectedFile(null);
                    }}
                    className="p-2 rounded-full hover:bg-white/10 transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </motion.button>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-800 dark:text-gray-400">
                    Fotoğrafınızı değiştirin veya kaldırın
                  </p>
                </div>
              </div>

              <div className="p-6 pt-0 space-y-6">
                {isEditing ? (
                  <>
                    {/* Kırpma Editörü */}
                    <div className="flex justify-center">
                      <div className="relative rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-900/50 p-4 backdrop-blur-sm border border-white/10 dark:border-gray-700/50">
                        <ReactCrop
                          crop={crop}
                          onChange={(c) => setCrop(c)}
                          onComplete={(c) => setCompletedCrop(c)}
                          aspect={1}
                          circularCrop
                        >
                          <motion.img
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            src={URL.createObjectURL(selectedFile)}
                            onLoad={onImageLoad}
                            className="max-h-[300px] w-auto rounded-lg"
                          />
                        </ReactCrop>
                      </div>
                    </div>

                    {/* Kırpma İşlemi Butonları */}
                    <div className="flex justify-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setIsEditing(false);
                          setSelectedFile(null);
                        }}
                        className="px-4 py-2 rounded-xl text-gray-600 dark:text-gray-300 hover:bg-white/10 backdrop-blur-sm transition-colors"
                        disabled={isLoading}
                      >
                        İptal
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleSaveCrop}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl backdrop-blur-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            <span>Kaydediliyor...</span>
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4" />
                            <span>Kaydet</span>
                          </>
                        )}
                      </motion.button>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Mevcut Fotoğraf */}
                    <motion.div
                      initial={{ scale: 0.9 }}
                      animate={{ scale: 1 }}
                      className="flex justify-center"
                    >
                      <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-blue-500 rounded-full blur opacity-50 group-hover:opacity-75 transition duration-1000"></div>
                        <div className="relative w-32 h-32 rounded-full overflow-hidden border-2 border-white dark:border-gray-700 shadow-xl">
                          <img
                            src={
                              userProfile?.profile_photo_url ||
                              getDefaultProfilePhotoUrl(
                                userProfile?.first_name,
                                userProfile?.last_name
                              )
                            }
                            alt="Current profile"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </motion.div>

                    {/* Değiştir / Kaldır Butonları */}
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="file"
                        ref={photoInputRef}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setSelectedFile(file);
                            setIsEditing(true);
                          }
                        }}
                      />

                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => photoInputRef.current?.click()}
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white rounded-xl backdrop-blur-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                        disabled={isLoading}
                      >
                        <Upload className="w-4 h-4" />
                        <span>
                          {userProfile?.profile_photo_url ? 'Fotoğraf Değiştir' : 'Fotoğraf Ekle'}
                        </span>
                      </motion.button>

                      {userProfile?.profile_photo_url && (
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={handlePhotoRemove}
                          className="flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white rounded-xl backdrop-blur-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>Kaldırılıyor...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" />
                              <span>Kaldır</span>
                            </>
                          )}
                        </motion.button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;
